/**
 * Geofence distance utilities using the Haversine formula.
 * This is a direct port of the Go implementation in
 * internal/domain/services/geo/geofence_checker.go
 * to ensure client and server calculations agree.
 */
/** Mean radius of the Earth in meters. */
export const EARTH_RADIUS_METERS = 6371000;
/**
 * Calculate the distance in meters between two GPS coordinates
 * using the Haversine formula.
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
    const lat1Rad = (lat1 * Math.PI) / 180;
    const lat2Rad = (lat2 * Math.PI) / 180;
    const deltaLat = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_METERS * c;
}
/**
 * Check if a point is within a radius (in meters) of a center point.
 */
export function isWithinRadius(lat, lon, centerLat, centerLon, radiusMeters) {
    return haversineDistance(lat, lon, centerLat, centerLon) <= radiusMeters;
}
/**
 * A geofence place as the API sends it, made safe to use.
 *
 * The backend models coordinates as decimals, which serialize as JSON
 * strings ("40.12345"), while GeofencePlace types them as numbers. Code that
 * trusted the type crashed — `place.latitude.toFixed is not a function` on
 * the location geofence card, reached from the executive dashboard. Convert
 * at the boundary; a place without usable coordinates is no place.
 */
export function normalizeGeofencePlace(raw) {
    if (!raw)
        return null;
    const latitude = Number(raw.latitude);
    const longitude = Number(raw.longitude);
    if (raw.latitude === null || raw.longitude === null || raw.latitude === '' || raw.longitude === '')
        return null;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude))
        return null;
    return { ...raw, latitude, longitude };
}
